// Auth API service

import { apiClient } from './client'
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  SwitchTenantRequest,
  SwitchTenantResponse,
  TenantMembership,
  InvitationInfoResponse,
  User,
} from '@/types/auth'

// Dev-mode mock login response — used when VITE_INTERNAL_SECRET is set (direct service mode).
const devLoginResponse = (): LoginResponse => ({
  accessToken: 'dev-token-local',
  refreshToken: 'dev-refresh-local',
  expiresIn: 86400,
  user: {
    id: 'user-00000001',
    email: 'dev@vharta.local',
    fullName: 'Dev User',
    role: 'tenant_owner' as User['role'],
    tenantId: '00000000-0000-0000-0000-000000000001',
    permissions: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  availableTenants: [
    {
      tenantId: '00000000-0000-0000-0000-000000000001',
      tenantName: 'test',
      role: 'tenant_owner' as User['role'],
      isPlatform: false,
    } as TenantMembership,
  ],
  requiresTenantSelection: false,
})

export const authApi = {
  /**
   * Login with email and password
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    if (import.meta.env.VITE_INTERNAL_SECRET) {
      return Promise.resolve(devLoginResponse())
    }
    return apiClient.post<LoginResponse>('/auth/login', data)
  },

  /**
   * Register a new user
   */
  register: async (data: RegisterRequest): Promise<LoginResponse> => {
    return apiClient.post<LoginResponse>('/auth/register', data)
  },

  /**
   * Logout the current user
   */
  logout: async (opts?: { signal?: AbortSignal; headers?: Record<string, string> }): Promise<void> => {
    return apiClient.post('/auth/logout', undefined, opts)
  },

  /**
   * Refresh the authentication token
   */
  refreshToken: async (data: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
    if (import.meta.env.VITE_INTERNAL_SECRET) {
      return Promise.resolve({ accessToken: 'dev-token-local', refreshToken: 'dev-refresh-local', expiresIn: 86400 })
    }
    return apiClient.post<RefreshTokenResponse>('/auth/refresh', data)
  },

  /**
   * Get the current user profile. Includes the server-authoritative
   * `auth_method` ('sso' | 'local') indicating how the session was
   * established — the client must not infer this by decoding the JWT.
   */
  me: async (): Promise<User> => {
    if (import.meta.env.VITE_INTERNAL_SECRET) {
      return Promise.resolve(devLoginResponse().user)
    }
    return apiClient.get<User>('/auth/me')
  },

  /**
   * Update the current user profile
   */
  updateProfile: async (data: { fullName?: string; avatarUrl?: string; timezone?: string }): Promise<{ success: boolean; message: string; user: Partial<User> }> => {
    return apiClient.patch('/auth/profile', data)
  },

  /**
   * Change password for the current user
   */
  changePassword: async (data: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
    return apiClient.post('/auth/change-password', data)
  },

  /**
   * Request a password reset email
   */
  forgotPassword: async (data: ForgotPasswordRequest): Promise<void> => {
    return apiClient.post('/auth/forgot-password', data)
  },

  /**
   * Reset password with token
   */
  resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
    return apiClient.post('/auth/reset-password', data)
  },

  /**
   * Get invitation info (validates token, returns details for UI routing)
   */
  getInvitationInfo: async (token: string): Promise<InvitationInfoResponse> => {
    return apiClient.get<InvitationInfoResponse>(`/auth/invitation/info?token=${encodeURIComponent(token)}`)
  },

  /**
   * Decline an invitation (ownership refusal)
   */
  declineInvitation: async (token: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.post('/auth/invitation/decline', { token })
  },

  /**
   * Accept tenant ownership (verified users only — no password needed)
   */
  acceptOwnership: async (token: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.post('/auth/accept-ownership', { token })
  },

  /**
   * Verify email with token
   */
  verifyEmail: async (token: string): Promise<void> => {
    return apiClient.post('/auth/verify-email', { token })
  },

  /**
   * Resend verification email
   */
  resendVerificationEmail: async (): Promise<void> => {
    return apiClient.post('/auth/resend-verification')
  },

  /**
   * SSO logout — revokes the SSO session cookie on the backend.
   * Called when the current session was established via SSO.
   */
  ssoLogout: async (opts?: { signal?: AbortSignal; headers?: Record<string, string> }): Promise<void> => {
    return apiClient.post('/sso/logout', undefined, opts)
  },

  /**
   * Sign-out by the SSO refresh cookie alone. The cookie is path-scoped to
   * /sso/refresh, so POST /sso/logout (which needs a valid access JWT and never
   * receives that cookie) cannot end it: once the access token has expired the
   * 7-day refresh credential would otherwise stay live in the browser.
   */
  ssoRefreshRevoke: async (opts?: { signal?: AbortSignal; headers?: Record<string, string> }): Promise<void> => {
    return apiClient.delete('/sso/refresh', opts)
  },

  /**
   * SSO silent refresh (WS-11): exchanges the HttpOnly vharta_sso_rt refresh
   * cookie for a fresh vharta_session access cookie. Cookie-only — no token in
   * the request or response body. Backend endpoint per
   * docs/plans/SSO-SILENT-REFRESH-WS11.md (falls through safely if not yet built).
   */
  ssoRefresh: async (): Promise<void> => {
    return apiClient.post('/sso/refresh')
  },

  /**
   * Switch to a different tenant context
   */
  switchTenant: async (data: SwitchTenantRequest): Promise<SwitchTenantResponse> => {
    return apiClient.post<SwitchTenantResponse>('/auth/switch-tenant', data)
  },

  /**
   * Get all tenants the current user belongs to
   */
  getUserTenants: async (): Promise<TenantMembership[]> => {
    return apiClient.get<TenantMembership[]>('/auth/user-tenants')
  },
}
